'use client';

console.warn('[WorkspaceContext] Module loaded at', new Date().toISOString());

/**
 * Workspace Context Provider
 * Manages the current workspace and provides workspace-related functionality
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Workspace, WorkspaceMetadata } from './workspace-types';
import {
  getCurrentWorkspace,
  setCurrentWorkspaceId,
  getAllWorkspaces,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  getWorkspaceStats,
  initializeWorkspaces,
} from './storage/workspace-db';
import type { WorkspaceStats, CreateWorkspaceParams, UpdateWorkspaceParams } from './workspace-types';

interface WorkspaceContextValue {
  currentWorkspace: Workspace | null;
  workspaces: WorkspaceMetadata[];
  stats: WorkspaceStats | null;
  loading: boolean;
  switchWorkspace: (id: string) => Promise<void>;
  createNewWorkspace: (params: CreateWorkspaceParams) => Promise<Workspace>;
  updateCurrentWorkspace: (params: UpdateWorkspaceParams) => Promise<void>;
  deleteWorkspaceById: (id: string) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceMetadata[]>([]);
  const [stats, setStats] = useState<WorkspaceStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize workspaces on mount
  useEffect(() => {
    async function init() {
      console.warn('[WorkspaceContext] Starting initialization');
      try {
        await initializeWorkspaces();
        console.warn('[WorkspaceContext] initializeWorkspaces complete');
        const current = await getCurrentWorkspace();
        console.warn('[WorkspaceContext] getCurrentWorkspace:', current?.id, current?.name);
        setCurrentWorkspace(current);

        const all = await getAllWorkspaces();
        console.warn('[WorkspaceContext] getAllWorkspaces:', all.length, 'workspaces');
        setWorkspaces(all);

        if (current) {
          const workspaceStats = await getWorkspaceStats(current.id);
          setStats(workspaceStats);
        }
        console.warn('[WorkspaceContext] Initialization complete');
      } catch (error) {
        console.error('Failed to initialize workspaces:', error);
        // Still try to set a workspace even if there was an error
        try {
          const all = await getAllWorkspaces();
          setWorkspaces(all);
          if (all.length > 0) {
            setCurrentWorkspace(all[0]);
            console.warn('[WorkspaceContext] Recovery: set workspace to', all[0].id);
          }
        } catch (e) {
          console.error('Failed to recover from initialization error:', e);
        }
      } finally {
        console.warn('[WorkspaceContext] Setting loading=false');
        setLoading(false);
      }
    }

    init();
  }, []);

  const refreshWorkspaces = async () => {
    const all = await getAllWorkspaces();
    setWorkspaces(all);

    if (currentWorkspace) {
      const workspaceStats = await getWorkspaceStats(currentWorkspace.id);
      setStats(workspaceStats);
    }
  };

  const switchWorkspace = async (id: string) => {
    const workspace = workspaces.find(w => w.id === id);
    if (!workspace) return;

    setCurrentWorkspaceId(id);
    setCurrentWorkspace(workspace);

    const workspaceStats = await getWorkspaceStats(id);
    setStats(workspaceStats);

    // Trigger a page reload to refresh diagrams
    window.location.reload();
  };

  const createNewWorkspace = async (params: CreateWorkspaceParams): Promise<Workspace> => {
    const workspace = await createWorkspace(params, false);
    await refreshWorkspaces();
    return workspace;
  };

  const updateCurrentWorkspace = async (params: UpdateWorkspaceParams): Promise<void> => {
    if (!currentWorkspace) return;

    await updateWorkspace(currentWorkspace.id, params);
    const updated = await getCurrentWorkspace();
    setCurrentWorkspace(updated);
    await refreshWorkspaces();
  };

  const deleteWorkspaceById = async (id: string): Promise<void> => {
    await deleteWorkspace(id);
    await refreshWorkspaces();

    // If we deleted the current workspace, switch to default
    if (currentWorkspace?.id === id) {
      const remaining = await getAllWorkspaces();
      const defaultWs = remaining.find(w => w.isDefault) || remaining[0];
      if (defaultWs) {
        await switchWorkspace(defaultWs.id);
      }
    }
  };

  const value: WorkspaceContextValue = {
    currentWorkspace,
    workspaces,
    stats,
    loading,
    switchWorkspace,
    createNewWorkspace,
    updateCurrentWorkspace,
    deleteWorkspaceById,
    refreshWorkspaces,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
